import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, from } from 'rxjs';
import { environment } from 'src/environments/environment';
import { API } from 'aws-amplify';
import { DEFAULT_GET_LIMIT } from '../shared/constants/operationals';

export const PRODUCT_CATEGORIES_API_NAME: string = 'product_categories';
export const PRODUCT_CATEGORIES_API_ENDPOINT: string = environment.API_BASE_PATH + '/product-category';

@Injectable({
  providedIn: 'root',
})
export class ProductCategoryService {
  constructor(private fb: FormBuilder) {}

  buildProductCategoryForm(form_group: FormGroup, default_lengths: number[]): FormGroup {
    form_group.setControl(
      'installment_plans',
      this.fb.array(
        default_lengths.map(length =>
          this.fb.group({
            plan_length: [length, Validators.required],
            step_0_tax_percentage: [
              null,
              [Validators.required, Validators.min(Math.ceil((100 / length) * 2) / 2), Validators.max(100)],
            ],
          }),
        ),
      ),
    );
    return form_group;
  }

  listProductCategories(list_params?: ListProductCategoryParams): Observable<any> {
    const api_name: string = PRODUCT_CATEGORIES_API_NAME;
    const path: string = '';
    let query_string_params: any = {
      cursor: list_params?.cursor || null,
      limit: list_params?.limit || DEFAULT_GET_LIMIT,
    };
    const init = { response: true, queryStringParameters: query_string_params };
    return from(API.get(api_name, path, init));
  }

  createProductCategory(product_category: ProductCategoryCreateInput): Observable<any> {
    const api_name: string = PRODUCT_CATEGORIES_API_NAME;
    const path: string = '';
    const init = { response: true, body: product_category };
    return from(API.post(api_name, path, init));
  }

  deleteProductCategory(product_category_id: string): Observable<any> {
    const api_name: string = PRODUCT_CATEGORIES_API_NAME;
    const path: string = `/${product_category_id}`;
    const init = { response: true };
    return from(API.del(api_name, path, init));
  }

  updateProductCategory(product_category_id: string, product_category: ProductCategoryUpdateInput): Observable<any> {
    const api_name: string = PRODUCT_CATEGORIES_API_NAME;
    const path: string = `/${product_category_id}`;
    const init = { response: true, body: product_category };
    return from(API.put(api_name, path, init));
  }

  addSubCategory(product_category_id: string, subcategory: SubCategoryInput): Observable<any> {
    const api_name: string = PRODUCT_CATEGORIES_API_NAME;
    const path: string = `/${product_category_id}/sub-category`;
    const init = { response: true, body: { sub_category: subcategory } };
    return from(API.post(api_name, path, init));
  }

  deleteSubCategory(product_category_id: string, sub_category_id: string): Observable<any> {
    const api_name: string = PRODUCT_CATEGORIES_API_NAME;
    const path: string = `/${product_category_id}/sub-category/${sub_category_id}`;
    const init = { response: true };
    return from(API.del(api_name, path, init));
  }

  updateSubCategory(
    product_category_id: string,
    sub_category_id: string,
    subcategory: SubCategoryInput,
  ): Observable<any> {
    const api_name: string = PRODUCT_CATEGORIES_API_NAME;
    const path: string = `/${product_category_id}/sub-category/${sub_category_id}`;
    const init = { response: true, body: { name: subcategory.name, ecommerce_default: subcategory.ecommerce_default } };
    return from(API.put(api_name, path, init));
  }

  readProductCategory(product_category_id: string): Observable<any> {
    const api_name: string = PRODUCT_CATEGORIES_API_NAME;
    const path: string = `/${product_category_id}`;
    const init = { response: true };
    return from(API.get(api_name, path, init));
  }

  listAllProductCategories(): Observable<any> {
    const api_name: string = PRODUCT_CATEGORIES_API_NAME;
    const path: string = `/list-all`;
    const init = { response: true };
    return from(API.get(api_name, path, init));
  }
}

export interface SubCategory {
  sub_category_id?: string;
  name: string;
  ecommerce_default: boolean;
}

export interface SubCategoryInput {
  name: string;
  ecommerce_default: boolean;
}

export interface InstallmentPlan {
  plan_length: number;
  step_0_tax_percentage: number;
}

export interface ProductCategory {
  product_category_id?: string;
  name: string;
  decido_evaluation_code: string;
  sub_category_collection: SubCategory[];
  installment_plans: InstallmentPlan[];
  object?: string;
  created_at?: number;
  updated_at?: number;
}

export interface ProductCategoryCreateInput {
  name?: string;
  decido_evaluation_code?: string;
  sub_category_collection?: { name: string }[];
  installment_plans?: InstallmentPlan[];
}

export interface ProductCategoryUpdateInput {
  name?: string;
  decido_evaluation_code?: string;
  installment_plans?: InstallmentPlan[];
}

export interface ListProductCategoryParams {
  cursor?: string | null;
  limit?: number;
}
